import React, { useState } from 'react';

const CategoriesTab = ({
  categories,
  tips,
  setShowAddCategory,
  editingCategory,
  setEditingCategory,
  updateCategory,
  deleteCategory,
  isDarkMode
}) => {
  const [editForm, setEditForm] = useState({
    label: '',
    emoji: '',
    color: 'blue',
    isActive: true
  });

  const colorOptions = [
    { name: 'blue', bg: 'bg-blue-100', text: 'text-blue-700' },
    { name: 'green', bg: 'bg-green-100', text: 'text-green-700' },
    { name: 'purple', bg: 'bg-purple-100', text: 'text-purple-700' },
    { name: 'red', bg: 'bg-red-100', text: 'text-red-700' },
    { name: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-700' },
    { name: 'pink', bg: 'bg-pink-100', text: 'text-pink-700' },
    { name: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-700' },
    { name: 'orange', bg: 'bg-orange-100', text: 'text-orange-700' }
  ];

  const handleEditClick = (cat) => {
    setEditingCategory(cat.id);
    setEditForm({
      label: cat.label || '',
      emoji: cat.emoji || '📚',
      color: cat.color || 'blue',
      isActive: cat.isActive !== false
    });
  };

  const handleSaveEdit = (catId) => {
    updateCategory(catId, editForm);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditForm({ label: '', emoji: '', color: 'blue', isActive: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Categories for organizing tips (words, facts, jokes, idioms, etc.)
        </p>
        <button onClick={() => setShowAddCategory(true)} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700">
          + Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {categories.map(cat => {
          const isEditing = editingCategory === cat.id;
          const tipCount = tips.filter(t => t.category === cat.name).length;
          
          return (
            <div key={cat.id} className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
              {isEditing ? (
                // Edit Mode
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Label</label>
                      <input
                        type="text"
                        value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        placeholder="Category label"
                      />
                    </div>
                    <div className="w-20">
                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Emoji</label>
                      <input
                        type="text"
                        value={editForm.emoji}
                        onChange={(e) => setEditForm({ ...editForm, emoji: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg border text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                        placeholder="📚"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Color</label>
                    <div className="flex gap-2 mt-1">
                      {colorOptions.map(color => (
                        <button
                          key={color.name}
                          onClick={() => setEditForm({ ...editForm, color: color.name })}
                          className={`w-6 h-6 rounded-full ${color.bg} ${editForm.color === color.name ? 'ring-2 ring-offset-2 ring-purple-500' : ''}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active</label>
                    <button
                      onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
                      className={`w-10 h-5 rounded-full transition-colors ${editForm.isActive ? 'bg-green-500' : 'bg-gray-400'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${editForm.isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleSaveEdit(cat.id)}
                      className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                    >
                      ✓ Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 px-3 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{cat.emoji}</span>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cat.label}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{cat.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${cat.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {tipCount} tips
                      </span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleEditClick(cat)}
                      className={`flex-1 px-3 py-1.5 text-sm rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => deleteCategory(cat.id)}
                      className="flex-1 px-3 py-1.5 text-sm rounded-lg bg-red-100 hover:bg-red-200 text-red-700"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {categories.length === 0 && (
        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p className="text-4xl mb-2">📂</p>
          <p>No categories yet. Click "Add Category" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default CategoriesTab;
